import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Heart, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { SiteNav } from "@/components/SiteChrome";
import { WAITLIST_LINE, WaitlistButton } from "@/components/Waitlist";

const Stars = ({ value, set }) => (
  <div className="flex gap-1" data-testid="pr-stars">{[1, 2, 3, 4, 5].map((n) => (
    <button key={n} type="button" onClick={() => set(n)} aria-label={`${n} stars`} data-testid={`pr-star-${n}`} className="transition-transform hover:scale-110">
      <Star className={`h-9 w-9 ${n <= value ? "fill-sun text-sun" : "text-border"}`} />
    </button>
  ))}</div>
);

const Invite = () => (
  <div className="mt-8 rounded-3xl border-2 border-pinkb/30 bg-card p-6 text-left" data-testid="pr-club-invite">
    <div className="eyebrow mb-1">Keep the momentum going</div>
    <p className="font-serif uppercase text-2xl text-navy">Join The Roots Club</p>
    <p className="mt-2 text-sm text-foreground/80">On-demand marketing answers from Lindsay's knowledge base, templates for every stage, and your next Mini Marketing Plan for just £59.</p>
    <div className="mt-5 flex flex-wrap items-center gap-4"><WaitlistButton testId="pr-club-waitlist" className="px-6 py-2.5 text-sm" />
      <Link to="/the-roots-club" data-testid="pr-club-link" className="inline-flex items-center gap-1 text-sm text-pinkb hover:underline">Find out more <ArrowRight className="h-4 w-4" /></Link></div>
    <p className="mt-3 text-xs text-muted-foreground">{WAITLIST_LINE}</p>
  </div>
);

const Thanks = ({ member, status }) => (
  <div className="text-center fade-up" data-testid="pr-thanks">
    <Heart className="h-10 w-10 text-pinkb mx-auto" />
    <h1 className="font-serif uppercase text-3xl text-navy mt-4">Thank you.</h1>
    <p className="text-muted-foreground mt-3">{status === "pending" ? "Lindsay reads every one. Once she's approved your review, it'll appear on the website." : "Lindsay reads every one, and she'll be in touch if you asked for help."}</p>
    {!member && <Invite />}
  </div>
);

export default function PlanReview() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ rating: 0, progress: "", most_useful: "", comment: "", help_needed: "", display_name: "", publish_ok: true });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  useEffect(() => {
    api.get(`/plan-review/${token}`).then((r) => { setD(r.data); setF((x) => ({ ...x, display_name: r.data.name || "" })); }).catch((e) => setErr(errMsg(e)));
  }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.rating) return toast.error("Please pick a star rating");
    setBusy(true);
    try { const r = await api.post(`/plan-review/${token}`, f); setSent(r.data); window.scrollTo({ top: 0 }); } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="relative z-10 min-h-screen bg-background"><SiteNav />
      <div className="max-w-2xl mx-auto px-6 py-14" data-testid="plan-review">
        {err && <p className="rounded-xl bg-rose-50 border border-rose-200 p-4" data-testid="pr-error">{err}. Please use the link from your email, or contact lindsay@rootsmarketingco.uk.</p>}
        {d && (sent || d.done ? <Thanks member={d.member} status={sent?.status} /> : (
          <form onSubmit={submit} className="space-y-8 fade-up" data-testid="pr-form">
            <div>
              <div className="eyebrow mb-3">Mini Marketing Plan · one week on</div>
              <h1 className="font-serif uppercase text-4xl text-navy leading-[0.95]">How's your plan going{d.name ? `, ${d.name}` : ""}?</h1>
              <p className="mt-3 text-muted-foreground">Two minutes. Your honest answers help Lindsay make every plan better.</p>
            </div>
            <div className="space-y-2"><p className="font-medium text-navy">How would you rate your plan?</p><Stars value={f.rating} set={(n) => setF({ ...f, rating: n })} /></div>
            <div className="space-y-2"><p className="font-medium text-navy">Where are you with it?</p>
              <div className="flex flex-wrap gap-2">{d.progress_options.map((o, i) => (
                <button key={o} type="button" onClick={() => setF({ ...f, progress: f.progress === o ? "" : o })} data-testid={`pr-progress-${i}`}
                  className={`rounded-full border-2 px-4 py-2 text-sm transition-colors ${f.progress === o ? "border-pinkb bg-pinkb/5 text-navy" : "border-border bg-white hover:border-pinkb/50"}`}>{o}</button>
              ))}</div>
            </div>
            <label className="block space-y-2"><span className="font-medium text-navy">What's been most useful so far?</span><Textarea rows={3} value={f.most_useful} onChange={set("most_useful")} data-testid="pr-most-useful" className="bg-white" /></label>
            <div className="rounded-2xl bg-secondary p-5 space-y-4">
              <label className="block space-y-2"><span className="font-medium text-navy">Your review for the website</span>
                <span className="block text-xs text-muted-foreground">A sentence or two other founders would find helpful. Optional.</span>
                <Textarea rows={3} value={f.comment} onChange={set("comment")} data-testid="pr-comment" className="bg-white" /></label>
              <label className="block space-y-2"><span className="text-sm text-navy">Show my name as{d.business ? ` (shown with ${d.business})` : ""}</span><Input value={f.display_name} onChange={set("display_name")} data-testid="pr-display-name" className="bg-white" /></label>
              <label className="flex items-start gap-2 text-sm"><Checkbox checked={f.publish_ok} onCheckedChange={(v) => setF({ ...f, publish_ok: !!v })} data-testid="pr-publish-ok" className="mt-0.5" />Lindsay can share my review on her website</label>
            </div>
            <label className="block space-y-2"><span className="font-medium text-navy">Anything you're stuck on, or want help with?</span>
              <span className="block text-xs text-muted-foreground">Private: only Lindsay sees this.</span>
              <Textarea rows={3} value={f.help_needed} onChange={set("help_needed")} data-testid="pr-help" className="bg-white" /></label>
            <Button type="submit" disabled={busy} data-testid="pr-submit" className="rounded-full bg-pinkb hover:bg-pinkb/90 text-white px-8">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Send to Lindsay</Button>
          </form>
        ))}
      </div>
    </div>
  );
}
